import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { Suspense, lazy, useState, useCallback } from 'react'
import { enumsQueryOptions, useInfiniteLocationSearchQuery } from '@/api/accommodation/hooks'
import Search from '@/features/accommodation/search/components/search'

// Lazy so mapbox-gl (~1.8 MB) loads only when the map view is opened, never in
// the default list view.
const SearchMap = lazy(() => import('@/features/accommodation/search/components/SearchMap'))
import {
  locationSearchSchema,
  useLocationSearchParams,
  FILTER_DEFAULTS,
  type LocationSearchParams,
  type SortOrder,
  type SortDirection,
} from '@/features/accommodation/hooks/useLocatiomSearchParams'
import {
  trackSearchFiltersApplied,
  trackSearchSortChanged,
  trackSearchFiltersCleared,
  trackSearchViewTimeSpent,
} from '@/lib/track'
import { useVisibleTimeTracking } from '@/hooks/useVisibleTimeTracking'
import { MobileSearchSummary } from '@/features/accommodation/search/components/MobileSearchSummary'
import { SearchFiltersFormDesktop } from '@/features/accommodation/search/components/SearchFiltersForm'
import { SortSelect, SortSelectMobileContent } from '@/features/accommodation/search/components/SortSelect'
import { getSortLabel } from '@/features/accommodation/search/components/sortOptions'
import Box from '@/components/box'
import { useIsMobile } from '@/hooks/useIsMobile'
import { ArrowUpDownIcon, ListIcon, MapIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer'
import { buildSearchHead } from '@/lib/seo/staticPages'

export const Route = createFileRoute('/_layout/ubytovani/hledat')({
  component: AccommodationSearchPage,
  validateSearch: locationSearchSchema,
  search: {
    // Strip the values locationSearchSchema fills in via .catch() back out of
    // the URL, so the canonical filter-less URL stays clean (no SSR redirect
    // to `?region=all&…`). Inline literal — a cross-module const would be
    // evaluated too late in the code-split production bundle.
    middlewares: [
      stripSearchParams({
        region: 'all',
        beds: 1,
        cribs: 0,
        adult: false,
        pets: false,
        skip: 0,
        take: 12,
        order: 'start' as SortOrder,
        direction: 'asc' as SortDirection,
      }),
    ],
  },
  // Enums resolve the region/category slugs to IDs, so a shared filtered link
  // fetches the right offers at once and the type chips render server-side.
  // prefetch, not ensure: without enums the page still works, just unfiltered.
  loader: ({ context }) => context.queryClient.query(enumsQueryOptions()).catch(() => undefined),
  // Canonical points at the clean, filter-less URL so the many faceted
  // combinations (region, dates, beds…) consolidate onto one indexable page.
  head: () => buildSearchHead(),
})

const PAGE_SIZE = 24

function ViewToggleButton({
  view,
  onToggle,
  className,
}: {
  view: 'list' | 'map'
  onToggle: () => void
  className?: string
}) {
  return (
    <Button
      onClick={onToggle}
      className={cn('flex h-auto items-center gap-2 rounded-full px-6 py-3.5 shadow-lg shadow-slate-600/30', className)}
    >
      {view === 'map' ? <ListIcon className="size-5" /> : <MapIcon className="size-5" />}
      <span className="text-base font-semibold">{view === 'map' ? 'Seznam' : 'Mapa'}</span>
    </Button>
  )
}

function AccommodationSearchInner() {
  const { filters, setFilters } = useLocationSearchParams()
  const isMobile = useIsMobile()
  const view = Route.useSearch({ select: (s) => s.view ?? 'list' })
  const navigate = Route.useNavigate()
  useVisibleTimeTracking(useCallback((seconds: number) => trackSearchViewTimeSpent(view, seconds), [view]))
  const accommodationSearchQuery = useInfiniteLocationSearchQuery(filters, PAGE_SIZE)

  // Local state for temporary filter changes (desktop)
  const [localFilters, setLocalFilters] = useState<LocationSearchParams>(filters)
  const [sortDrawerOpen, setSortDrawerOpen] = useState(false)
  const [mapCardOpen, setMapCardOpen] = useState(false)

  const updateLocalFilters = useCallback(
    (updates: Partial<LocationSearchParams>, commit = false) => {
      const newState = { ...localFilters, ...updates }
      setLocalFilters(newState)
      if (commit) setFilters(newState)
    },
    [localFilters, setFilters],
  )

  const commitFilters = useCallback(() => {
    trackSearchFiltersApplied(localFilters)
    setFilters(localFilters)
  }, [localFilters, setFilters])

  const clearFilters = useCallback(() => {
    const defaultFilters: LocationSearchParams = {
      fromDate: undefined,
      toDate: undefined,
      region: FILTER_DEFAULTS.region,
      labelId: undefined,
      beds: 1,
      cribs: 0,
      adult: false,
      pets: false,
      facility: undefined,
      skip: 0,
      take: 12,
      order: 'start',
      direction: 'asc',
    }
    setLocalFilters(defaultFilters)
    setFilters(defaultFilters)
    trackSearchFiltersCleared()
  }, [setFilters])

  // Map opening is tracked inside SearchMap (on mount, once fully loaded),
  // which also covers deep links and reloads with view=map.
  const toggleView = useCallback(() => {
    const nextView = view === 'map' ? 'list' : 'map'
    void navigate({
      search: (prev) => ({
        ...prev,
        view: nextView === 'map' ? nextView : undefined,
      }),
      resetScroll: false,
    })
  }, [view, navigate])

  const handleSortChange = useCallback(
    (order: SortOrder, direction: SortDirection) => {
      const updated = { ...filters, order, direction }
      setLocalFilters(updated)
      setFilters(updated)
      setSortDrawerOpen(false)
      trackSearchSortChanged(order, direction)
    },
    [filters, setFilters],
  )

  return (
    <div className="relative">
      {/* Visually hidden — the search UI has no place for a visible heading,
          but crawlers require an H1 on the page. */}
      <h1 className="sr-only">Vyhledávání last-minute ubytování – chalupy, chaty a penziony</h1>

      {/* Mobile: Sticky search summary */}
      <div className="sticky top-[env(safe-area-inset-top)] z-40 container py-2 lg:hidden">
        <div className="-mx-2">
          <MobileSearchSummary />
        </div>
      </div>

      {/* Desktop: Full filter UI */}
      <div className="container mt-4 mb-8 hidden pt-4 lg:block">
        <Box className="rounded-3xl py-6 lg:px-8">
          <SearchFiltersFormDesktop
            localFilters={localFilters}
            filters={filters}
            updateLocalFilters={updateLocalFilters}
            commitFilters={commitFilters}
          />
        </Box>
      </div>

      {/* Floating map view toggle (list view only; in map view it sits inside the map) */}
      {view === 'list' && (
        <ViewToggleButton
          view={view}
          onToggle={toggleView}
          className="fixed right-4 bottom-6 z-40 lg:right-auto lg:left-1/2 lg:-translate-x-1/2"
        />
      )}

      {/* Mobile: Floating sort button */}
      {isMobile && view === 'list' && (
        <>
          <Button
            onClick={() => setSortDrawerOpen(true)}
            className="fixed bottom-6 left-4 z-40 -ml-2 flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 shadow-lg shadow-slate-600/20 hover:bg-white lg:hidden"
          >
            <ArrowUpDownIcon className="h-4 w-4 text-slate-700" />
            <span className="text-sm font-semibold text-slate-900">
              {getSortLabel(filters.order, filters.direction)}
            </span>
          </Button>
          <Drawer open={sortDrawerOpen} onOpenChange={setSortDrawerOpen}>
            <DrawerContent className="h-auto gap-0">
              <DrawerHeader>
                <DrawerTitle>Řazení</DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-4">
                <SortSelectMobileContent
                  order={filters.order}
                  direction={filters.direction}
                  onChange={handleSortChange}
                />
              </div>
            </DrawerContent>
          </Drawer>
        </>
      )}

      {/* Search results */}
      <div className="container mt-4 mb-8">
        {/* Desktop: Sort controls */}
        {!isMobile && view === 'list' && (
          <div className="mb-4 flex items-center justify-end">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Seřadit</span>
              <SortSelect order={filters.order} direction={filters.direction} onChange={handleSortChange} />
            </div>
          </div>
        )}
        {view === 'map' ? (
          <div className="relative">
            <Suspense
              fallback={
                <div className="h-[calc(100dvh-13rem)] min-h-[420px] animate-pulse rounded-2xl bg-slate-100 lg:h-[calc(100dvh-22rem)]" />
              }
            >
              <SearchMap
                queryResult={accommodationSearchQuery}
                onClearFilters={clearFilters}
                filters={filters}
                onCardOpenChange={setMapCardOpen}
                className="h-[calc(100dvh-13rem)] min-h-[420px] lg:h-[calc(100dvh-22rem)]"
              />
            </Suspense>
            {/* On mobile an open offer card takes the toggle's place. */}
            {!(isMobile && mapCardOpen) && (
              <ViewToggleButton view={view} onToggle={toggleView} className="absolute right-4 bottom-6 z-20" />
            )}
          </div>
        ) : (
          <Search
            queryResult={accommodationSearchQuery}
            pageSize={PAGE_SIZE}
            onClearFilters={clearFilters}
            filters={filters}
            showSimilar
          />
        )}
      </div>
    </div>
  )
}

function AccommodationSearchPage() {
  return (
    <Suspense>
      <AccommodationSearchInner />
    </Suspense>
  )
}
